<script setup lang="ts">
import EnglishLessonPage from "~/features/english/components/EnglishLessonPage.vue";

const textbookPoints = [
  "Text me Apollo 11 ko sirf 3 astronauts ki story nahi balki 400,000 logon ki collective effort ke tor par present kiya gaya hai.",
  "Playtex ki seamstresses, NASA engineers, Mission Control team, Margaret Hamilton aur background workers sab mission ki success me central role nibhate hain.",
  "Lesson ka main theme teamwork, collaboration, foresight, precision aur calm decision-making hai.",
];

const chapterCoverage = [
  "Apollo 11 as collective effort",
  "Neil Armstrong's moon step in wider context",
  "Playtex seamstresses aur spacesuit precision",
  "Saturn V rocket engineers",
  "Mission Control roles and preparation",
  "Margaret Hamilton and flight software",
  "Teamwork, hidden contributors, and planning",
  "Textbook flowchart / mind-map activity",
  "Setting-based passage analysis",
];

const quickRevisionPoints = [
  "Apollo 11 ki success sirf astronauts ki nahi balkeh 400,000 logon ki effort thi.",
  "Playtex seamstresses ki stitching astronaut safety ke liye critical thi.",
  "Mission Control ne emergency scenarios pehle se rehearse kiye hue thay.",
  "Margaret Hamilton ke software ne overload ke waqt critical tasks ko priority di.",
  "Lesson ka strongest message teamwork aur unseen contributors ki importance hai.",
];

const answerBuilding = [
  "Agar question hidden contributors se related ho to seamstresses, engineers aur software team tino ka zikr karo.",
  "Mission Control wale answer me pressure, discipline aur role clarity ko ek sath explain karo.",
  "Team Moon ki summary me visible heroes ke sath unseen workers ko zaroor include karo.",
  "Margaret Hamilton ke answer me software overload aur safe landing ka relation likho.",
];

const textbookExerciseMap = [
  "While-reading section me 6 direct comprehension questions aati hain: Armstrong, seamstresses, Saturn V, Mission Control roles, emergency preparation, aur Margaret Hamilton.",
  "Reading and Critical Thinking section me 8 broader questions aati hain jin me author ka purpose, Mission Control setting, tone words, hidden contributors aur summary / mind-map shamil hote hain.",
  "Chapter ke baad passage-based setting questions bhi hain jo Mission Control aur space-race context ko separately explain karwati hain.",
];

const textbookFollowUps = [
  "Mind-map / flowchart activity me main nodes: astronauts, seamstresses, engineers, Mission Control, software, alarms, landing.",
  "Setting answers me teen cheezen saath rakhna strong hota hai: pressure, urgency, aur trained decision-making.",
  "Teamwork theme wale answer me visible heroes aur hidden contributors ka contrast use karna textbook tone ke zyada qareeb hai.",
];

const whileReadingQuestions = [
  {
    question: "What was significant about Neil Armstrong's first steps on the Moon?",
    answer: "Un ki first step ne history bana di aur human achievement ka global symbol ban gayi. Magar lesson yeh bhi dikhata hai ke us moment ke peechay bohat bari collective team effort thi.",
  },
  {
    question: "Why was the job of the seamstresses at Playtex so important for the Apollo 11 mission?",
    answer: "Unhon ne aise spacesuits stitch kiye jo movement bhi allow karen aur vacuum of space se protection bhi dein. Ek bhi faulty stitch astronauts ki jaan ke liye khatra ban sakti thi.",
  },
  {
    question: "What kind of problems were the NASA engineers trying to solve with the Saturn V rocket?",
    answer: "Woh rocket stability, space navigation precision, aur astronauts ko safely wapas lane jese unprecedented technical problems solve kar rahe thay.",
  },
  {
    question: "Why was it important for each member of the Mission Control team to have a specific role during the mission?",
    answer: "Mission bohat high-stakes tha, is liye har person ka clearly defined role fast aur accurate decision-making ke liye zaroori tha. Is se emergency me confusion kam hui aur response quick raha.",
  },
  {
    question: "How did the team at Mission Control prepare for unexpected problems during the Apollo 11 mission?",
    answer: "Unhon ne possible failures aur emergency aborts tak ke scenarios practice kiye. Isi preparation ki wajah se alarms aane par woh panic ke bajaye calmly react kar sake.",
  },
  {
    question: "Who was Margaret Hamilton, and what was her role in the Apollo 11 mission?",
    answer: "Margaret Hamilton software engineer thi jinhon ne lunar module ke onboard flight software ki team lead ki. Un ke code ne overload ke waqt critical tasks ko priority di aur landing ko bachaya.",
  },
];

const readingQuestions = [
  {
    question: "Why does the author choose to focus on the seamstresses at Playtex when discussing the Apollo 11 mission?",
    answer: "Author dikhana chahta hai ke mission ki success sirf famous astronauts ki wajah se nahi thi. Seamstresses ki contribution se yeh message milta hai ke hidden workers bhi extraordinary achievements me equally important hote hain.",
  },
  {
    question: "How does the setting of Mission Control shape the actions and decisions of the flight controllers?",
    answer: "Mission Control ka tense aur data-heavy environment flight controllers ko disciplined, alert aur decisive banata hai. Har second important hone ki wajah se un ki judgment, teamwork aur calmness aur zyada meaningful ho jati hai.",
  },
  {
    question: "What impact do words like tension, critical, and overloaded have on the tone of the narrative?",
    answer: "Yeh words narrative me urgency, suspense aur danger ka tone create karte hain. Reader ko mehsoos hota hai ke mission ke key moments me slightest error bhi disaster ka sabab ban sakta tha.",
  },
  {
    question: "What would be the impact if Team Moon had focused solely on the astronauts' perspectives?",
    answer: "Agar focus sirf astronauts par hota to story ka broader message weak ho jata. Text ka core idea yeh hai ke giant achievement teamwork ka result thi, na ke sirf visible heroes ka.",
  },
  {
    question: "What is the author's purpose in highlighting the lesser-known contributors to Apollo 11?",
    answer: "Author traditional hero-centered narrative ko challenge karta hai aur batata hai ke technicians, engineers, coders, cleaners aur seamstresses sab mission ki chain ka essential hissa thay.",
  },
  {
    question: "What might have happened if the engineers at NASA had failed to solve a key problem with the Saturn V rocket?",
    answer: "Mission delay, abort, ya complete failure tak ho sakta tha. Rocket engineering issues solve na hote to astronauts ki safety aur poori moon landing risk me par jati.",
  },
  {
    question: "How can you summarise the main points of Team Moon?",
    answer: "Apollo 11 ki success 400,000 logon ki teamwork, innovation, preparation aur perseverance ka result thi. Har contributor, chahe famous ho ya unseen, mission ko possible banane me important tha.",
  },
  {
    question: "What elements should be included in a flowchart or mind-map of Team Moon?",
    answer: "Is me astronauts, seamstresses, engineers, Mission Control, Margaret Hamilton, problem-solving, teamwork, precision aur successful landing jese major nodes hone chahiye.",
  },
];

const settingPassageAnswers = [
  {
    question: "How does the setting of Mission Control influence the actions, emotions and decisions of the characters working there?",
    answer: "Mission Control ka pressure-filled setting characters ko hyper-focused aur disciplined rakhta hai. Un ki emotions tense hoti hain, magar professional training ki wajah se woh panic ke bajaye structured decisions lete hain.",
  },
  {
    question: "How does the high-stakes environment of Mission Control drive the plot forward?",
    answer: "Isi environment ki wajah se alarms, overload aur real-time decisions plot me suspense create karte hain. Setting plot ko static rehne nahi deti aur har moment ko consequential bana deti hai.",
  },
  {
    question: "How does the historical setting of the space race influence the motivations of the characters and the urgency of the mission?",
    answer: "Space race ka era national pride, scientific competition aur global attention se loaded tha. Isi wajah se characters ki motivation aur mission ki urgency dono bohat intense nazar aati hain.",
  },
];

const mcqs = [
  {
    question: "What is the main idea of Team Moon?",
    options: [
      { label: "a", text: "Apollo 11 succeeded because of astronauts alone.", verdict: "incorrect", reason: "Chapter specifically hero-only narrative ko challenge karta hai." },
      { label: "b", text: "Moon landing was the result of a huge collaborative effort.", verdict: "correct", reason: "Text repeatedly teamwork, hidden contributors, aur collective preparation ko central banata hai." },
      { label: "c", text: "Mission Control had little role in the mission.", verdict: "incorrect", reason: "Mission Control ko major decision-making center ke tor par show kiya gaya hai." },
      { label: "d", text: "Luck mattered more than planning.", verdict: "incorrect", reason: "Preparation, precision, aur foresight chapter ke strongest themes hain." },
    ],
  },
  {
    question: "Why were the Playtex seamstresses important to Apollo 11?",
    options: [
      { label: "a", text: "They designed the rocket engines.", verdict: "incorrect", reason: "Rocket engineering un ka role nahi tha." },
      { label: "b", text: "They made spacesuits whose stitching had to be extremely precise.", verdict: "correct", reason: "Chapter un ke stitching precision ko astronauts ki safety se directly connect karta hai." },
      { label: "c", text: "They operated Mission Control consoles.", verdict: "incorrect", reason: "Mission Control roles alag thay." },
      { label: "d", text: "They wrote all of NASA's flight software.", verdict: "incorrect", reason: "Software role Margaret Hamilton aur un ki team se associate hota hai." },
    ],
  },
  {
    question: "Margaret Hamilton is highlighted because she:",
    options: [
      { label: "a", text: "piloted the lunar module herself", verdict: "incorrect", reason: "Woh astronaut nahi thi." },
      { label: "b", text: "led the team that developed critical flight software", verdict: "correct", reason: "Unit me un ka software role overload situation me mission-saving factor ke tor par explain hota hai." },
      { label: "c", text: "stitched the astronauts' gloves", verdict: "incorrect", reason: "Yeh seamstresses ka contribution tha." },
      { label: "d", text: "announced the landing on television", verdict: "incorrect", reason: "Chapter ka focus software engineering par hai, broadcast role par nahi." },
    ],
  },
];

usePageSeo({
  title: "Team Moon Notes | 11th Class English Unit 4",
  description: "11th class English Team Moon notes with while-reading answers, reading and critical thinking, setting-based questions, revision points and explained MCQs.",
  keywords: "team moon notes, 11th class english unit 4, class 11 english chapter 4, team moon short questions, team moon mcqs, team moon summary",
  breadcrumbs: [
    { name: "Home", item: "/" },
    { name: "11th English", item: "/11th/english/" },
    { name: "Prose", item: "/11th/english/prose/" },
    { name: "Team Moon", item: "/11th/english/prose/team-moon/" },
  ],
});
</script>

<template>
  <EnglishLessonPage
    unit="Unit 4"
    title="Team Moon"
    intro="Yeh page textbook chapter ke reading and critical thinking sections par based hai. Focus Apollo 11 ki visible aur hidden team contributions ko samajhne par hai."
    :bullet-sections="[
      { title: 'Textbook-Aligned Snapshot', items: textbookPoints },
      { title: 'Unit 4 Coverage Checklist', items: chapterCoverage, layout: 'grid' },
      { title: 'Quick Revision Points', items: quickRevisionPoints },
      { title: 'Textbook Exercise Map', items: textbookExerciseMap },
      { title: 'Answer-Building Notes', items: answerBuilding },
      { title: 'Source-Backed Follow-Up Focus', items: textbookFollowUps }
    ]"
    :text-sections="[
      { title: 'While-Reading Questions with Answers', items: whileReadingQuestions.map((item) => ({ title: item.question, description: item.answer })) },
      { title: 'Reading and Critical Thinking', items: readingQuestions.map((item) => ({ title: item.question, description: item.answer })) },
      { title: 'Passage-Based Setting Answers', items: settingPassageAnswers.map((item) => ({ title: item.question, description: item.answer })) }
    ]"
    :mcq-sections="[
      { title: 'MCQs with Option Explanation', items: mcqs }
    ]"
  />
</template>





